import React, { useContext } from 'react';
import { Box, Button, Checkbox, Collapse, Typography } from '@mui/material';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import { ThemeContext } from 'src/store/ContextStore';
import LegendMenu from '../pedigree-sidebar/sections/LegendMenu';
import PhenotypesLegend from '../pedigree-sidebar/sections/PhenotypesLegend';
import GenotypesLegend from '../pedigree-sidebar/sections/GenotypesLegend';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// TGCLOUD-1663 / 1674 — legend visibility, as a modal off the pedigree panel.
//
// This was an accordion in the record sidebar, which is the wrong home for the
// same reason the label toggles were: it is a display setting for the chart,
// not information about the family. Putting tick boxes on the legend itself was
// considered and rejected on appearance, so they live here.
//
// This modal is CONFIGURATION ONLY. The drag-a-disease-onto-a-person
// affordance that the accordion also carried is not lost — the legend on the
// pedigree panel renders the same components with `draggable`, so assignment
// keeps working where the chart is actually visible. Passing draggable here
// would mean dragging onto a canvas the dialog is covering.

const GroupHeader = ({
  title,
  checked,
  onToggle,
  onShowAll,
  someHidden,
  theme
}) => {
  const t = useAutoTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Checkbox
        size="small"
        checked={checked}
        onChange={(e) => onToggle(e.target.checked)}
        slotProps={{
          input: { 'aria-label': t('Show all {{title}}', { title }) }
        }}
      />
      <Typography
        variant="body1"
        sx={{ fontWeight: 'bold', flexGrow: 1, color: theme?.header }}
      >
        {title}
      </Typography>
      {someHidden && (
        <Button
          size="small"
          variant="text"
          onClick={onShowAll}
          sx={{ fontSize: '0.7rem', py: 0, px: 0.5, minWidth: 'auto' }}
        >
          {t('Show all')}
        </Button>
      )}
    </Box>
  );
};

const LegendConfigModal = ({
  open,
  handleClose,
  labelsSet,
  setLabelsSet,
  users,
  // TGCLOUD-1793 Phase 2: the family disease list with the site colour key
  // already overlaid. Passed in rather than derived from `users` here, so the
  // key is applied in ONE place and this modal's swatches cannot drift from the
  // chart's fills. Falls back to the raw list so the modal still works if a
  // caller has not been updated.
  diagnoses: diagnosesWithColourKey = users?.diagnoses,
  // Whether the site colour key is switched on for this group, so the signpost
  // below is shown only where it is true.
  colourKeyEnabled = false,
  memberRecords,
  setMemberRecords,
  carrierPresentStates,
  showCarrierMarks,
  carrierStyle
}) => {
  const t = useAutoTranslation();

  const { theme } = useContext(ThemeContext);

  // Same snapshot-and-restore contract as the label modal: the chart updates
  // live so the user can see what a tick does, and Cancel puts it back.
  const snapshotRef = React.useRef(null);
  React.useEffect(() => {
    if (open && !snapshotRef.current) snapshotRef.current = labelsSet;
    if (!open) snapshotRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Setting the master checkbox drives every item's map entry, so "Diseases
  // off" means no disease rows rather than a master flag the item list ignores.
  const setAll = (masterKey, mapKey, checked) =>
    setLabelsSet((prev) => ({
      ...prev,
      [masterKey]: checked,
      [mapKey]: Object.keys(prev[mapKey] || {}).reduce((acc, k) => {
        acc[k] = checked;
        return acc;
      }, {})
    }));

  const someHidden = (masterKey, mapKey) =>
    labelsSet?.[masterKey] === false ||
    Object.values(labelsSet?.[mapKey] || {}).some((v) => v === false);

  const onCancel = () => {
    if (snapshotRef.current) setLabelsSet(snapshotRef.current);
    handleClose();
  };

  return (
    <CustomDialog
      open={open}
      handleClose={onCancel}
      title="Legend"
      // Wider than the default: a disease or phenotype name plus its ontology
      // id and case count is a long line, and wrapping every row to fit a
      // narrow dialog made the list harder to scan than the chart legend it
      // configures.
      maxWidth={680}
      showActions
      saveBtnLabel="Done"
      cancelBtnLabel="Cancel"
      isParentHandlingLoading
      isParentLoading={false}
      onSave={() => {
        // Nothing to persist: legend visibility is per-session chart state,
        // exactly as it was in the sidebar. "Done" just closes.
        handleClose();
        return Promise.resolve();
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {t(
            'Choose what appears in the legend. Phenotype and genotype labels on the pedigree are controlled separately, under Labels. Switching diseases off also removes their shading from the chart, because the legend is the only key to it.'
          )}
        </Typography>

        {/* TGCLOUD-1793 Phase 3: the entry point from the pedigree panel. A
            SIGNPOST, deliberately not an editor. This modal persists nothing —
            it is session-only legend visibility — whereas pinning a colour
            writes group-wide configuration that changes every clinician's
            charts. Those two things must not look like the same control, and
            the second belongs where the rest of the group's settings live. */}
        {colourKeyEnabled && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {t('Disease colours for this whole group are set under')}{' '}
            <strong>{t('Settings › Pedigree › Disease colours')}</strong>
            {t('. Colours changed here are not saved.')}
          </Typography>
        )}

        <Box>
          <GroupHeader
            title={t('Diseases')}
            theme={theme}
            checked={labelsSet?.diseases ?? true}
            onToggle={(checked) => setAll('diseases', 'diseasesMap', checked)}
            onShowAll={() => setAll('diseases', 'diseasesMap', true)}
            someHidden={someHidden('diseases', 'diseasesMap')}
          />
          <Collapse in>
            <LegendMenu
              diagnoses={diagnosesWithColourKey}
              id="diagnoses-legend-modal"
              labelsSet={labelsSet}
              setLabelsSet={setLabelsSet}
              showLegendTxt
              showCheckboxes
              showMasterCheckbox={false}
              dataset={memberRecords}
              carrierPresentStates={carrierPresentStates}
              showCarrierMarks={showCarrierMarks}
              carrierStyle={carrierStyle}
              showRecordLegend
            />
          </Collapse>
        </Box>

        {users?.phenotypes?.length > 0 && (
          <Box>
            <GroupHeader
              title={t('Phenotypes')}
              theme={theme}
              checked={labelsSet?.phenotypes ?? true}
              onToggle={(checked) =>
                setAll('phenotypes', 'phenotypesMap', checked)
              }
              onShowAll={() => setAll('phenotypes', 'phenotypesMap', true)}
              someHidden={someHidden('phenotypes', 'phenotypesMap')}
            />
            <Collapse in>
              <PhenotypesLegend
                phenotypes={users.phenotypes}
                id="phenotypes-legend-modal"
                dataset={memberRecords}
                setDataset={setMemberRecords}
                labelsSet={labelsSet}
                setLabelsSet={setLabelsSet}
                showCheckboxes
              />
            </Collapse>
          </Box>
        )}

        {users?.genotypes?.length > 0 && (
          <Box>
            <GroupHeader
              title={t('Genotypes')}
              theme={theme}
              checked={labelsSet?.genotypes ?? true}
              onToggle={(checked) =>
                setAll('genotypes', 'genotypesMap', checked)
              }
              onShowAll={() => setAll('genotypes', 'genotypesMap', true)}
              someHidden={someHidden('genotypes', 'genotypesMap')}
            />
            <Collapse in>
              <GenotypesLegend
                genotypes={users.genotypes}
                id="genotypes-legend-modal"
                dataset={memberRecords}
                setDataset={setMemberRecords}
                labelsSet={labelsSet}
                setLabelsSet={setLabelsSet}
                showCheckboxes
              />
            </Collapse>
          </Box>
        )}
      </Box>
    </CustomDialog>
  );
};

export default LegendConfigModal;
